import { useCallback, useMemo, useRef, useState } from 'react';

import { CONSENT_DEFINITIONS } from 'constants/user-constants';
import { useTranslation } from 'react-i18next';
import { UserConsentModal } from 'views/Users/Consent/UserConsentModal';

export const useFCRAConsentModal = ({ onConsentSubmit, isBatchImport = false } = {}) => {
  const { t } = useTranslation();
  const pendingChecksDataRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [isMultiParty, setIsMultiParty] = useState(false);
  const [isBatch, setIsBatch] = useState(isBatchImport);

  const buildConsentList = useCallback(
    consentKeys => {
      return consentKeys.map(key => {
        const def = CONSENT_DEFINITIONS[key];

        return {
          id: def.id,
          version: def.activeVersion,
          content: t(`users.consent.${def.id}.content`),
          checked: false,
        };
      });
    },
    [t],
  );

  const consents = useMemo(() => {
    if (isMultiParty) {
      return buildConsentList(['fcra_multiparty_consent', 'fcra_standard']);
    }

    if (isBatch) {
      return buildConsentList(['fcra_batch_consent']);
    }

    return buildConsentList(['fcra_capture_consent']);
  }, [isMultiParty, isBatch, buildConsentList]);

  const showModal = useCallback(checksData => {
    pendingChecksDataRef.current = checksData ?? null;
    setVisible(true);
  }, []);

  const hideModal = useCallback(() => {
    setVisible(false);
  }, []);

  const handleConsentSubmit = useCallback(
    data => {
      setVisible(false);
      const checksData = pendingChecksDataRef.current;
      onConsentSubmit?.(data, checksData);
      pendingChecksDataRef.current = null;
    },
    [onConsentSubmit],
  );

  const FCRAConsentModal = useMemo(
    () => (
      <UserConsentModal
        visible={visible}
        onClose={hideModal}
        consents={consents}
        onSubmit={handleConsentSubmit}
      />
    ),
    [visible, consents, handleConsentSubmit, hideModal],
  );

  return {
    showModal,
    hideModal,
    isVisible: visible,
    FCRAConsentModal,
    isMultiParty,
    setIsMultiParty,
    isBatch,
    setIsBatch,
    consents,
  };
};
